// App.tsx (excerpt)
import React, { useEffect } from "react";
import "./App.css";
import AppRoutes from "./app/AppRoutes";
import { useClarityIdentity } from "./hooks/useClarityIdentity";
import { useAuth0 } from "@auth0/auth0-react";
import {
  addAccessTokenInterceptor,
  addCompanyUuidInterceptor,
} from "./api/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserAppSettingsAction } from "./modules/hub/features/UserAppSettings/slices/userAppSettingsSlice";
import { ThemeProvider } from "./context/ThemeContext";
import { useNotification } from "./context/NotificationContext";
import axiosInstance from "./api/axiosInstance";
import { Toaster } from "@/components/ui/toaster";
import type { RootState } from "./app/rootReducer";
import FileManagerGlobalDialog from "@/components/GlobalDialogs/FileManagerGlobalDialog";
import { LoadFailurePage } from "./components/LoadFailurePage/LoadFailurePage";
import MembershipInactivePage from "@/modules/hub/features/Auth/components/MembershipInactivePage/MembershipInactivePage";
import Logout from "@/modules/hub/features/Auth/components/Logout/Logout";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";

function App() {
  useClarityIdentity();
  const dispatch = useDispatch();

  const {
    isLoading: authLoading,
    getAccessTokenSilently,
    isAuthenticated,
    loginWithRedirect,
  } = useAuth0();

  const { showNotification } = useNotification();

  useEffect(() => {
    addAccessTokenInterceptor(
      axiosInstance,
      getAccessTokenSilently,
      loginWithRedirect,
      showNotification,
    );
    addCompanyUuidInterceptor(axiosInstance, getAccessTokenSilently);
  }, [getAccessTokenSilently, loginWithRedirect, showNotification]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUserAppSettingsAction(true));
    }
  }, [isAuthenticated, dispatch]);

  const {
    userAppSettings,
    loading: userAppSettingsLoading,
    error: userAppSettingsError,
  } = useSelector((state: RootState) => state.userAppSettings);

  if (authLoading || (isAuthenticated && userAppSettingsLoading)) {
    return <LoadingIndicator />;
  }

  if (isAuthenticated && !userAppSettings && !userAppSettingsLoading) {
    // Even when the bootstrap fails, signing out must stay reachable — the
    // Logout component also clears any impersonation metadata, without
    // which an impersonated admin stays stuck as the impersonated user
    // across sign-ins.
    if (window.location.pathname === "/logout") {
      return <Logout />;
    }

    // The membership gate 403s the bootstrap too; the axios interceptor has
    // already routed to /membership-inactive, so render that screen rather
    // than the deactivated-account one.
    if (window.location.pathname === "/membership-inactive") {
      return <MembershipInactivePage />;
    }

    // A deactivated user (no active employee) fails the bootstrap with 403.
    const isDeactivated = userAppSettingsError?.includes("403");
    const isNoAccount =
      userAppSettingsError?.includes("404") ||
      userAppSettingsError?.toLowerCase().includes("not found");
    const reason = isDeactivated
      ? "deactivated"
      : isNoAccount
        ? "no_account"
        : "load_error";

    return <LoadFailurePage reason={reason} />;
  }

  return (
    <ThemeProvider>
      <AppRoutes />
      <Toaster />
      <FileManagerGlobalDialog />
    </ThemeProvider>
  );
}

export default App;
